// src/modules/app-views/leads/pages/Init/modals/DistributeLeadsModal/index.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useState, useEffect, useMemo } from 'react';
import { SwapOutlined } from '@ant-design/icons';
import GenericModal from 'components/core/GenericModal';
import translateX from 'utils/translateX';
import leadsService from 'modules/app-views/leads/service';
import crmService from 'modules/app-views/crm/service';
import appService from 'services/appService';
import axiosService from 'services/axiosService';
import entitiesService from 'services/entitiesService';
import { mergeUrlParamsQuery } from 'utils/urlUtils';
import { API } from 'constants/Api';
import Step1 from './Step1';

// *******************************************************************************
// HELPERS
// *******************************************************************************
function getAllBoards() {
  const boards = appService.getModulePreferences({ moduleId: 'crm', category: 'boards' });
  return Array.isArray(boards) ? boards.filter(b => !b.removedAt && !b.archivedAt) : [];
}

function buildAllBoardOptions() {
  return getAllBoards().map(b => ({
    value: b.uid,
    label: b.title,
    color: b.backColor || '#1890ff',
  }));
}

// Filter boards that have at least one of the given offer IDs in their linkedOfferIds
function filterBoardsByOfferIds(offerIds) {
  if (!offerIds.size) return [];
  return getAllBoards()
    .filter(b => (b.linkedOfferIds || []).some(id => offerIds.has(id)))
    .map(b => ({ value: b.uid, label: b.title, color: b.backColor || '#1890ff' }));
}

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const DistributeLeadsModal = ({ show, setShow, leads = [], afterClose, onSuccess }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [boardOptions, setBoardOptions] = useState([]);
  const [loadingBoards, setLoadingBoards] = useState(true);
  const [boardStatus, setBoardStatus] = useState('ok'); // 'ok' | 'no_offers' | 'no_board_in_offers'

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  // Fetch offers associated with the leads' landing pages to filter board options
  useEffect(() => {
    if (!leads?.length) {
      setBoardOptions(buildAllBoardOptions());
      setLoadingBoards(false);
      setBoardStatus('ok');
      return;
    }

    // Collect unique landingPageIds from all leads being distributed
    const landingPageIds = [
      ...new Set(leads.map(l => l.landingPageId).filter(Boolean)),
    ];

    if (!landingPageIds.length) {
      setBoardOptions(buildAllBoardOptions());
      setLoadingBoards(false);
      setBoardStatus('ok');
      return;
    }

    setLoadingBoards(true);

    // Fetch all offers linked to any of the leads' landing pages
    Promise.all(
      landingPageIds.map(lpId => {
        const url = mergeUrlParamsQuery({
          url: API.OFFERS.GET_BY_FIELD,
          query: { field: 'linkedLandingPageIds', value: lpId, mode: 'arrayContains' },
        });
        return axiosService.get({ url }).then(([docs]) => docs || []);
      })
    )
      .then(results => {
        const offers = results.flat();

        if (!offers.length) {
          // No offers linked to any of the lead's landing pages
          setBoardStatus('no_offers');
          setBoardOptions([]);
          return;
        }

        // Collect IDs of offers that match the lead's landing pages
        const matchedOfferIds = new Set(offers.map(o => o._id).filter(Boolean));

        // Find boards that have at least one of those offer IDs in their linkedOfferIds
        const filtered = filterBoardsByOfferIds(matchedOfferIds);

        if (filtered.length) {
          setBoardStatus('ok');
          setBoardOptions(filtered);
        } else {
          // Offers exist for this landing page but no board has them linked
          setBoardStatus('no_board_in_offers');
          setBoardOptions([]);
        }
      })
      .catch(() => {
        // On error, assume no offers
        setBoardStatus('no_offers');
        setBoardOptions([]);
      })
      .finally(() => setLoadingBoards(false));
  }, [leads]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────
  const confirmDisabled = boardStatus !== 'ok' && !loadingBoards;

  const steps = useMemo(
    () => [buildStep1(leads, boardOptions, loadingBoards, boardStatus)],
    [leads, boardOptions, loadingBoards, boardStatus]
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleFinish = async ({ form, setLoading }) => {
    const { consultantId, boardId } = form.getFieldsValue(true);

    if (!boardId) {
      appService.message('e', translateX('select_board'));
      return false;
    }

    if (!consultantId) {
      appService.message('e', translateX('select_consultant'));
      return false;
    }

    setLoading(true);

    // Resolve first column of the selected board for the CRM opportunity
    const board = crmService.getBoardByUid(boardId);
    const boardColumns = board?.columns || [];
    const sortedColumns = [...boardColumns].sort((a, b) => (a.order || 0) - (b.order || 0));
    const firstColumnUid = sortedColumns[0]?.uid || '';
    const boardFormFields = board?.formFields || [];

    try {
      for (const lead of leads) {
        // 1. Assign lead (backend expects accountId for the consultant)
        await leadsService.assignLead(lead._id, { accountId: consultantId, boardId });

        // 2. Resolve entity: match existing by CPF → phone → email, or create new
        //    Also ensures lead's phone/email are in the entity and returns the primary contact ID
        const { entityId, primaryContactId } = await resolveEntityIdForLead(lead);

        // 3. Generate AI description + auto-fill formData (non-blocking — nulls on failure)
        const aiResult = await crmService.describeOpportunityFromLead(lead, boardFormFields);

        // 4. Create CRM opportunity linked to this lead
        const oppData = buildOpportunityData(lead, boardId, firstColumnUid, consultantId, entityId, primaryContactId);
        if (aiResult?.note) oppData.note = aiResult.note;
        if (aiResult?.formData) oppData.formData = aiResult.formData;
        await crmService.addOpportunity(oppData);

        // 5. Archive the lead now that it has been converted to a CRM opportunity
        await leadsService.archiveLead(lead._id);
      }

      appService.message({ flag: 's', text: 'leads_assigned_successfully' });
      onSuccess?.();
    } catch (error) {
      const msg = error?.response?.data?.message || error?.message;
      appService.message({ flag: 'e', text: msg || 'error_saving_document' });
      setLoading(false);
      return false;
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <GenericModal
      show={show}
      setShow={setShow}
      title={translateX('distribute_leads')}
      icon={<SwapOutlined />}
      steps={steps}
      onFinish={handleFinish}
      afterClose={afterClose}
      confirmDisabled={confirmDisabled}
      width={480}
    />
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default DistributeLeadsModal;

// *******************************************************************************
// HELPERS
// *******************************************************************************
// Build CRM opportunity payload from a lead document + selection data.
// primaryContactId = _id of the contact subdocument (phone) inside the entity's contacts array.
// This tells the CRM which phone/channel to use for calls and WhatsApp by default.
// contactPhone/Email/UniqueId are frozen snapshots of the lead's contact info at distribution time.
function buildOpportunityData(lead, boardUid, columnUid, consultantId, entityId, primaryContactId) {
  return {
    opportunity: lead.interest || lead.name || '',
    opportunityType: 'sale',
    status: 'backlog',
    entityId: entityId || null,
    personContactId: primaryContactId || null,
    consultantId,
    consultantIds: [consultantId],
    attachmentIds: [],
    callCount: 0,
    boardUid,
    columnUid,
    // Lead contact snapshot — frozen at distribution time, used in CRM card header and outreach
    contactPhone: (lead.phone || '').replace(/\D/g, '') || null,
    contactEmail: lead.email || null,
    contactUniqueId: lead.uniqueId || lead.cpf || null,
    // Traceability fields linking back to the origin lead and landing page
    leadId: lead._id,
    landingPageId: lead.landingPageId || null,
    _metadata: { origin: 2 }, // 2 = automatic (system-generated)
  };
}

// Resolve the entity for a lead:
//   1. Return lead.entityId if already linked
//   2. Match by CPF (uniqueId / cpfCnpj field)
//   3. Match by phone (contacts.value)
//   4. Match by email (contacts.value)
//   5. If matched: ensure 'opportunity' is in entityProfile; ensure lead phone/email exist in contacts
//   6. If no match: create new entity with entityProfile ['opportunity']
// Returns { entityId, primaryContactId } — primaryContactId is the _id of the phone contact
// subdocument in the entity's contacts array (used as personContactId in the CRM opportunity).
async function resolveEntityIdForLead(lead) {
  if (lead.entityId) return { entityId: lead.entityId, primaryContactId: null };
  if (!lead.name && !lead.phone && !lead.email) return { entityId: null, primaryContactId: null };

  let existingEntity = null;

  // Step 1: match by CPF (uniqueId field in lead maps to cpfCnpj in entity)
  const cpfNum = (lead.uniqueId || lead.cpf || '').replace(/\D/g, '');
  if (cpfNum) {
    existingEntity = await safeGetEntityByField(cpfNum, 'cpfCnpj');
  }

  // Step 2: match by phone
  const phoneNum = (lead.phone || '').replace(/\D/g, '');
  if (phoneNum && !existingEntity) {
    existingEntity = await safeGetEntityByField(phoneNum, 'contacts.value');
  }

  // Step 3: match by email
  const email = (lead.email || '').toLowerCase().trim();
  if (email && !existingEntity) {
    existingEntity = await safeGetEntityByField(email, 'contacts.value');
  }

  if (existingEntity?._id) {
    // Ensure 'opportunity' is in entityProfile (no-op if already present)
    if (!existingEntity.entityProfile?.includes('opportunity')) {
      try {
        await entitiesService.addToSet({
          _id: existingEntity._id,
          operator: '$push',
          path: 'entityProfile',
          data: 'opportunity',
        });
      } catch (error) {
        console.error('[DistributeLeadsModal] addToSet entityProfile failed:', JSON.stringify(error?.response?.data || error?.message, null, 2));
      }
    }

    // Ensure lead's phone and email are present in entity contacts.
    // Returns the _id of the phone contact to use as primary in the opportunity.
    const primaryContactId = await ensureLeadContactsInEntity(existingEntity, lead);
    return { entityId: existingEntity._id, primaryContactId };
  }

  // No match — create new entity
  return await createEntityForLead(lead);
}

// Wrapper for entitiesService.getEntityByField that returns null instead of throwing.
async function safeGetEntityByField(value, field) {
  try {
    return await entitiesService.getEntityByField(value, field) || null;
  } catch {
    return null;
  }
}

// Ensures lead's phone and email are present in the entity's contacts array.
// Adds missing contacts via addToSet. Returns the _id of the phone contact subdocument
// (the lead's phone number) to be used as the primary contact in the CRM opportunity.
async function ensureLeadContactsInEntity(entity, lead) {
  let contacts = entity.contacts || [];
  let primaryContactId = null;

  // Handle phone: add if not present, capture _id as primary
  const phoneNum = (lead.phone || '').replace(/\D/g, '');
  if (phoneNum) {
    const existing = contacts.find(
      c => ['phone', 'mobile'].includes(c.contactType) && c.value?.replace(/\D/g, '') === phoneNum
    );
    if (existing) {
      primaryContactId = existing._id || null;
    } else {
      try {
        const updated = await entitiesService.addToSet({
          _id: entity._id,
          operator: '$push',
          path: 'contacts',
          data: { contactType: 'phone', value: phoneNum, countryCode: 'BR', phoneType: 'mobile' },
        });
        contacts = updated?.contacts || contacts;
        const added = contacts.find(c => c.value?.replace(/\D/g, '') === phoneNum);
        primaryContactId = added?._id || null;
      } catch (error) {
        console.error('[DistributeLeadsModal] addToSet phone failed:', JSON.stringify(error?.response?.data || error?.message, null, 2));
      }
    }
  }

  // Handle email: add if not present (not primary — phone takes priority)
  const email = (lead.email || '').toLowerCase().trim();
  if (email) {
    const existing = contacts.find(
      c => c.contactType === 'email' && c.value?.toLowerCase() === email
    );
    if (!existing) {
      try {
        await entitiesService.addToSet({
          _id: entity._id,
          operator: '$push',
          path: 'contacts',
          data: { contactType: 'email', value: email, countryCode: 'BR' },
        });
      } catch (error) {
        console.error('[DistributeLeadsModal] addToSet email failed:', JSON.stringify(error?.response?.data || error?.message, null, 2));
      }
    }
  }

  return primaryContactId;
}

// Create a person entity from lead data. Returns { entityId, primaryContactId } or nulls on failure.
async function createEntityForLead(lead) {
  try {
    const url = API.ENTITIES.URL.replace(':_id?', '');
    const [entity] = await axiosService.post({ url, data: buildEntityPayload(lead) });
    const entityId = entity?._id || null;

    // Find the phone contact _id from the newly created entity
    const phoneNum = (lead.phone || '').replace(/\D/g, '');
    const phoneContact = (entity?.contacts || []).find(
      c => c.value?.replace(/\D/g, '') === phoneNum
    );
    return { entityId, primaryContactId: phoneContact?._id || null };
  } catch (error) {
    console.error('[DistributeLeadsModal] Entity creation failed:', JSON.stringify(error?.response?.data || error?.message, null, 2));
    return { entityId: null, primaryContactId: null };
  }
}

// Build the entity POST payload from a lead document.
function buildEntityPayload(lead) {
  const contacts = [];

  if (lead.phone) {
    const phoneNum = (lead.phone || '').replace(/\D/g, '');
    contacts.push({
      contactType: 'phone',
      value: phoneNum,
      countryCode: 'BR',
      // Leads from landing pages are typically mobile; defaulting avoids backend rejection of null phoneType
      phoneType: 'mobile',
    });
  }

  if (lead.email) {
    contacts.push({
      contactType: 'email',
      value: (lead.email || '').toLowerCase().trim(),
      countryCode: 'BR',
    });
  }

  return {
    entityType: 'person',
    entityProfile: ['opportunity'],
    name: (lead.name || '').trim(),
    contacts,
    _metadata: { origin: 2 }, // 2 = automatic (system-generated)
  };
}

function buildStep1(leads, boardOptions, loadingBoards, boardStatus) {
  return {
    stepTitle: translateX('select_consultant'),
    content: () => (
      <Step1
        leads={leads}
        boardOptions={boardOptions}
        loadingBoards={loadingBoards}
        boardStatus={boardStatus}
      />
    ),
    validationFields: ['boardId', 'consultantId'],
  };
}
